import { Divider, Flex, SimpleGrid, Stack, Text } from "@mantine/core";
import { useEffect, useMemo } from "react";

import type { EligibleProduct } from "../../../../types";

import { useSalesContext } from "../../../../sales-context/SalesContext";
import { useCallDetailsStore, useIntegratedFlowStore, useProductOfferStore } from "../../../../stores";
import { EligibilityStatus } from "../../../../types";
import { NoOfferButton } from "./NoOfferButton";
import {
    categorizeProductsByType,
    formatIneligibleProductDisplay,
    getProductCardVariant,
    offerPresentedKey,
    pickUnpresentedOffers,
    shouldShowNoOfferButton,
} from "./OffersView.helpers";
import { CompactPlanCard } from "./plan-cards/CompactPlanCard";
import { FullPlanCard } from "./plan-cards/FullPlanCard";

// Module-scoped dedupe so the "Offer presented" breadcrumb fires at most once
// per (sessionId, productOfferId) pair within a tab session, even across
// component remounts (tab navigation, re-renders).
const presentedOffers = new Set<string>();

export function OffersView({ isChat = false }: { isChat?: boolean }) {
    const { sessionId, partner, pilots, onProductSelected, features, logger: salesLogger } = useSalesContext();
    const logger = useMemo(() => salesLogger.child({ module: "OffersView" }), [salesLogger]);
    const products = useProductOfferStore((state) => state.getProducts(sessionId));
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));
    const updateSelectedProduct = useProductOfferStore((state) => state.updateSelectedProduct);
    const getProductInfo = useProductOfferStore((state) => state.getProductInfo);
    const client = (useCallDetailsStore.getState().callDetails.CLIENT as string) ?? "";

    const { primary, secondary, ineligible } = categorizeProductsByType({
        products,
        partner,
        isVerizonCareEnabled: features.isVerizonCareEnabled,
        pilots,
        client,
    });

    useEffect(() => {
        const newOffers = pickUnpresentedOffers([...primary, ...secondary], presentedOffers, sessionId);
        for (const fields of newOffers) {
            presentedOffers.add(offerPresentedKey(fields.sessionId, fields.productOfferId));
            logger.info(fields, "Offer presented");
        }
    }, [primary, secondary, sessionId, logger]);

    const handleCardClick = (product: EligibleProduct) => {
        updateSelectedProduct(sessionId, product);
        onProductSelected?.();
    };

    const showIneligibleText = products.length > 0 && !products.some((p) => p.status === EligibilityStatus.Eligible);

    return (
        <>
            <Stack gap="0.75rem" p="sm">
                {primary.map((product, index) => (
                    <FullPlanCard
                        key={product.productSku}
                        saleProgress={product.saleProgress}
                        partner={partner}
                        product={getProductInfo(sessionId, product.productSku)}
                        onClick={() => handleCardClick(product)}
                        isSelected={selectedProduct?.productSku === product.productSku}
                        position={index}
                    />
                ))}

                {secondary.length > 0 ? (
                    <SimpleGrid cols={2} spacing="lg" p={0}>
                        {secondary.map((product, index) => {
                            const variant = getProductCardVariant({ product, isChat });
                            return variant === "full" ? (
                                <FullPlanCard
                                    key={product.productSku}
                                    saleProgress={product.saleProgress}
                                    partner={partner}
                                    product={getProductInfo(sessionId, product.productSku)}
                                    onClick={() => {
                                        handleCardClick(product);
                                        useIntegratedFlowStore.getState().resetIntegratedStepStore(sessionId);
                                    }}
                                    isSelected={selectedProduct?.productSku === product.productSku}
                                    position={index}
                                    showEligibleTag={isChat}
                                />
                            ) : (
                                <CompactPlanCard
                                    key={product.productSku}
                                    saleProgress={product.saleProgress}
                                    partner={partner}
                                    product={getProductInfo(sessionId, product.productSku)}
                                    onClick={() => {
                                        handleCardClick(product);
                                        useIntegratedFlowStore.getState().resetIntegratedStepStore(sessionId);
                                    }}
                                    isSelected={selectedProduct?.productSku === product.productSku}
                                    productSku={product.productSku}
                                    position={index}
                                />
                            );
                        })}
                    </SimpleGrid>
                ) : showIneligibleText ? (
                    <Text>Bummer - this customer isn&apos;t eligible for any offers.</Text>
                ) : null}

                {ineligible.length > 0 && (
                    <>
                        <Flex align="center" gap="sm">
                            <Text size="xs" c="var(--mantine-color-dark-2)">
                                Ineligible products
                            </Text>
                            <Divider size="xs" flex={1} color="var(--mantine-color-dark-4)" />
                        </Flex>

                        {ineligible.map((product) => (
                            <Text key={product.productSku} size="xs">
                                {formatIneligibleProductDisplay({ product, partner })}
                            </Text>
                        ))}
                    </>
                )}
            </Stack>
            {shouldShowNoOfferButton({ partner, selectedProduct }) && <NoOfferButton />}
        </>
    );
}
